<!--
  AscendQE: organisation tree for Directory (Org chart view).
  Sub-unit hierarchy from /api/v2/admin/subunits?mode=tree, people from the
  directory API placed under their sub-unit. Follows the WAI-ARIA tree view
  pattern: one tab stop, ↑/↓ move, → expands / enters, ← collapses / goes to
  parent, Home/End, * expands siblings, Enter opens a person. If the viewer
  cannot read the sub-unit tree (non-admins), people are grouped by sub-unit
  name instead.
-->
<template>
  <div class="aqe-org">
    <div class="aqe-org__bar">
      <div class="aqe-org__search">
        <i class="oxd-icon bi-search" aria-hidden="true"></i>
        <input
          v-model="query"
          type="search"
          placeholder="Find a person or team"
          aria-label="Find a person or team"
          autocomplete="off"
        />
        <span v-if="query" class="aqe-org__hits">{{ hitCount }} found</span>
      </div>
      <button type="button" class="aqe-org__btn" @click="setAll(true)">
        <i class="oxd-icon bi-arrows-expand"></i> Expand all
      </button>
      <button type="button" class="aqe-org__btn" @click="setAll(false)">
        <i class="oxd-icon bi-arrows-collapse"></i> Collapse all
      </button>
    </div>

    <div v-if="loading" class="aqe-org__loading" role="status">
      <span class="aqe-org__spin"></span> Loading organisation…
    </div>
    <ul
      v-else
      ref="tree"
      class="aqe-org__tree"
      role="tree"
      aria-label="Organisation"
      @keydown="onKey"
    >
      <tree-node
        v-for="n in visibleRoots"
        :key="n.key"
        :node="n"
        :level="1"
        :expanded="expanded"
        :active="activeKey"
        :query="q"
        @toggle="toggle"
        @focus-node="activeKey = $event"
        @open="openPerson"
      />
    </ul>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';

const TreeNode = {
  name: 'TreeNode',
  props: {
    node: Object,
    level: Number,
    expanded: Object,
    active: String,
    query: String,
  },
  emits: ['toggle', 'focus-node', 'open'],
  computed: {
    isOpen() {
      return !!this.expanded[this.node.key];
    },
    hasKids() {
      return this.node.children && this.node.children.length > 0;
    },
    match() {
      return !!this.query && this.node.label.toLowerCase().includes(this.query);
    },
  },
  template: `
    <li
      role="treeitem"
      :aria-level="level"
      :aria-expanded="hasKids ? String(isOpen) : undefined"
      :aria-selected="String(active === node.key)"
      :data-key="node.key"
      :tabindex="active === node.key ? 0 : -1"
      class="aqe-org__item"
      :class="['is-' + node.type, {'is-active': active === node.key, 'is-match': match}]"
      @focus.self="$emit('focus-node', node.key)"
      @click.stop="hasKids ? $emit('toggle', node.key) : $emit('open', node); $emit('focus-node', node.key)"
    >
      <div class="aqe-org__row">
        <span class="aqe-org__chev" aria-hidden="true">
          <i v-if="hasKids" class="oxd-icon bi-chevron-right"></i>
        </span>
        <span v-if="node.type === 'person'" class="aqe-org__avatar" :data-hue="node.hue">{{ node.initials }}</span>
        <span v-else class="aqe-org__unit" aria-hidden="true"><i class="oxd-icon" :class="level === 1 ? 'bi-building' : 'bi-diagram-3'"></i></span>
        <span class="aqe-org__label">
          <span class="aqe-org__name">{{ node.label }}</span>
          <span v-if="node.sub" class="aqe-org__sub">{{ node.sub }}</span>
        </span>
        <span v-if="node.type === 'unit'" class="aqe-org__count">{{ node.people }}</span>
      </div>
      <ul v-if="hasKids && isOpen" role="group">
        <tree-node
          v-for="c in node.children"
          :key="c.key"
          :node="c"
          :level="level + 1"
          :expanded="expanded"
          :active="active"
          :query="query"
          @toggle="$emit('toggle', $event)"
          @focus-node="$emit('focus-node', $event)"
          @open="$emit('open', $event)"
        />
      </ul>
    </li>`,
};
TreeNode.components = {'tree-node': TreeNode};

export default {
  name: 'OrgTree',
  components: {'tree-node': TreeNode},

  setup() {
    return {http: new APIService(window.appGlobal.baseUrl, '')};
  },

  data() {
    return {loading: true, roots: [], expanded: {}, activeKey: null, query: ''};
  },

  computed: {
    q() {
      return this.query.trim().toLowerCase();
    },
    visibleRoots() {
      if (!this.q) return this.roots;
      const prune = (n) => {
        const kids = (n.children || []).map(prune).filter(Boolean);
        return n.label.toLowerCase().includes(this.q) || kids.length
          ? {...n, children: kids}
          : null;
      };
      return this.roots.map(prune).filter(Boolean);
    },
    hitCount() {
      let c = 0;
      const walk = (n) => {
        if (n.type === 'person' && n.label.toLowerCase().includes(this.q)) c++;
        (n.children || []).forEach(walk);
      };
      this.roots.forEach(walk);
      return c;
    },
  },

  watch: {
    q(v) {
      if (v) this.setAll(true); // reveal matches
    },
  },

  async mounted() {
    try {
      const [units, people] = await Promise.all([
        this.http
          .request({
            method: 'GET',
            url: '/api/v2/admin/subunits',
            params: {mode: 'tree'},
            validateStatus: (s) => s < 500,
          })
          .then((r) => (r.status === 200 ? r.data.data : null)),
        this.http
          .request({
            method: 'GET',
            url: '/api/v2/directory/employees',
            params: {limit: 0},
          })
          .then((r) => r.data.data || []),
      ]);
      this.roots = this.build(units, people);
      if (this.roots[0]) {
        this.expanded = {[this.roots[0].key]: true};
        this.activeKey = this.roots[0].key;
      }
    } finally {
      this.loading = false;
    }
  },

  methods: {
    person(e) {
      const name = [e.firstName, e.lastName].filter(Boolean).join(' ');
      return {
        key: 'p' + e.empNumber,
        type: 'person',
        id: e.empNumber,
        label: name,
        sub: [e.jobTitle?.title, e.location?.name].filter(Boolean).join(' · '),
        initials:
          ((e.firstName || '')[0] || '') + ((e.lastName || '')[0] || ''),
        hue: String(e.empNumber % 6),
      };
    },
    build(units, people) {
      const byUnit = new Map();
      people.forEach((e) => {
        const k = e.subunit?.id ?? 'none';
        if (!byUnit.has(k)) byUnit.set(k, {name: e.subunit?.name, list: []});
        byUnit.get(k).list.push(this.person(e));
      });
      const sortP = (a) => a.sort((x, y) => x.label.localeCompare(y.label));
      const unassigned = byUnit.get('none')?.list || [];

      if (Array.isArray(units) && units.length) {
        const conv = (u) => {
          const kids = (u.children || []).map(conv);
          const ppl = sortP(byUnit.get(u.id)?.list || []);
          const people = ppl.length + kids.reduce((a, k) => a + k.people, 0);
          return {
            key: 'u' + u.id,
            type: 'unit',
            id: u.id,
            label: u.name,
            sub: u.unitId || '',
            children: [...kids, ...ppl],
            people,
          };
        };
        const roots = units.map(conv);
        if (unassigned.length && roots[0]) {
          roots[0].children.push({
            key: 'u-none',
            type: 'unit',
            label: 'Not assigned to a team',
            children: sortP(unassigned),
            people: unassigned.length,
          });
          roots[0].people += unassigned.length;
        }
        return roots;
      }
      // fallback: flat grouping by sub-unit name
      const groups = [...byUnit.entries()].map(([k, g]) => ({
        key: 'u' + k,
        type: 'unit',
        label: g.name || 'Not assigned to a team',
        children: sortP(g.list),
        people: g.list.length,
      }));
      return [
        {
          key: 'u-org',
          type: 'unit',
          label: 'Organisation',
          children: groups,
          people: people.length,
        },
      ];
    },
    toggle(key) {
      this.expanded = {...this.expanded, [key]: !this.expanded[key]};
    },
    setAll(open) {
      const all = {};
      const walk = (n) => {
        if (n.children && n.children.length) {
          all[n.key] = open;
          n.children.forEach(walk);
        }
      };
      this.roots.forEach(walk);
      if (!open && this.roots[0]) all[this.roots[0].key] = true;
      this.expanded = all;
    },
    openPerson(n) {
      if (n.type === 'person')
        navigate('/pim/viewPersonalDetails/empNumber/{id}', {id: n.id});
    },
    visibleItems() {
      return [...this.$refs.tree.querySelectorAll('[role=treeitem]')];
    },
    focusKey(key) {
      this.activeKey = key;
      this.$nextTick(() =>
        this.$refs.tree.querySelector(`[data-key="${key}"]`)?.focus(),
      );
    },
    onKey(e) {
      const items = this.visibleItems();
      const cur = e.target.closest('[role=treeitem]');
      const i = items.indexOf(cur);
      if (i < 0) return;
      const key = cur.dataset.key;
      const hasKids = cur.getAttribute('aria-expanded') !== null;
      const open = cur.getAttribute('aria-expanded') === 'true';
      const go = (el) => el && this.focusKey(el.dataset.key);
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          go(items[i + 1]);
          break;
        case 'ArrowUp':
          e.preventDefault();
          go(items[i - 1]);
          break;
        case 'Home':
          e.preventDefault();
          go(items[0]);
          break;
        case 'End':
          e.preventDefault();
          go(items[items.length - 1]);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (hasKids && !open) this.toggle(key);
          else if (open) this.$nextTick(() => go(this.visibleItems()[i + 1]));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (open) this.toggle(key);
          else go(cur.parentElement.closest('[role=treeitem]'));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (hasKids) this.toggle(key);
          else this.openPerson(this.find(key));
          break;
        case '*': {
          e.preventDefault();
          const parent = cur.parentElement;
          const next = {...this.expanded};
          [...parent.children].forEach((li) => {
            if (li.getAttribute('aria-expanded') !== null)
              next[li.dataset.key] = true;
          });
          this.expanded = next;
          break;
        }
      }
    },
    find(key) {
      let hit = null;
      const walk = (n) => {
        if (hit) return;
        if (n.key === key) hit = n;
        (n.children || []).forEach(walk);
      };
      this.roots.forEach(walk);
      return hit;
    },
  },
};
</script>

<style lang="scss">
/* unscoped: the recursive node component renders from a template string */
.aqe-org {
  padding: 4px 24px 24px;
  font-family: Inter, 'Nunito Sans', sans-serif;
}
.aqe-org__bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.aqe-org__search {
  flex: 1 1 260px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.1);
  color: #94a3b8;
  input {
    flex: 1;
    border: 0;
    outline: 0;
    font: 500 14px Inter, sans-serif;
    color: #0f172a;
    background: transparent;
  }
  &:focus-within {
    box-shadow: 0 0 0 1px #6366f1, 0 0 0 4px rgba(99, 102, 241, 0.14);
  }
}
.aqe-org__hits {
  font-size: 12px;
  color: #64748b;
}
.aqe-org__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.1);
  color: #334155;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
}
.aqe-org__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
  color: #64748b;
}
.aqe-org__spin {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.12);
  border-top-color: #6366f1;
  animation: aqe-org-spin 0.8s linear infinite;
}
@keyframes aqe-org-spin {
  to {
    transform: rotate(360deg);
  }
}
.aqe-org__tree,
.aqe-org__tree ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.aqe-org__tree {
  max-height: 64vh;
  overflow: auto;
  padding: 8px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.07);
}
.aqe-org__tree ul {
  position: relative;
  margin-left: 22px;
  padding-left: 12px;
  border-left: 1px dashed rgba(15, 23, 42, 0.14);
}
.aqe-org__item {
  outline: none;
  cursor: pointer;
}
/* the row shows focus; never ring the whole branch */
.aqe-org__item:focus,
.aqe-org__item:focus-visible {
  outline: none !important;
}
.aqe-org__row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 4px 10px 4px 4px;
  border-radius: 12px;
  transition: background-color 0.12s;
}
.aqe-org__item > .aqe-org__row:hover {
  background: #f8fafc;
}
.aqe-org__item:focus-visible > .aqe-org__row,
.aqe-org__item.is-active > .aqe-org__row {
  background: rgba(99, 102, 241, 0.08);
  box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.35);
}
.aqe-org__item.is-match > .aqe-org__row .aqe-org__name {
  background: #fef08a;
  border-radius: 4px;
}
.aqe-org__chev {
  flex: 0 0 18px;
  color: #94a3b8;
  i {
    display: inline-block;
    transition: transform 0.15s;
  }
}
.aqe-org__item[aria-expanded='true'] > .aqe-org__row .aqe-org__chev i {
  transform: rotate(90deg);
}
.aqe-org__unit {
  flex: 0 0 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #0f172a;
  color: #a7f3d0;
}
.aqe-org__avatar {
  flex: 0 0 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font: 700 11.5px Inter, sans-serif;
  text-transform: uppercase;
  &[data-hue='1'] {
    background: #dcfce7;
    color: #166534;
  }
  &[data-hue='2'] {
    background: #fef3c7;
    color: #92400e;
  }
  &[data-hue='3'] {
    background: #fce7f3;
    color: #9d174d;
  }
  &[data-hue='4'] {
    background: #e0f2fe;
    color: #075985;
  }
  &[data-hue='5'] {
    background: #ede9fe;
    color: #5b21b6;
  }
}
.aqe-org__label {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.aqe-org__name {
  color: #0f172a;
  font: 600 13.5px Inter, sans-serif;
  align-self: flex-start;
}
.is-unit > .aqe-org__row .aqe-org__name {
  font-weight: 700;
}
.aqe-org__sub {
  color: #64748b;
  font-size: 12px;
}
.aqe-org__count {
  padding: 2px 9px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font: 600 12px Inter, sans-serif;
}
</style>
